/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../api";
import { Query_Keys } from "../lib/queryKeys";

/**
 * Loads the Admin App module catalogue that drives the Module Access tick-boxes.
 *
 * The catalogue is a code-defined registry on the server, not user data: it only changes when the
 * app is deployed. So it is fetched once per session and never refetched on focus or remount, and
 * every caller shares that single cache entry rather than issuing a request of its own.
 *
 * @param {Object} options
 * @param {boolean} options.enabled fetch only once something actually needs the catalogue
 */
export const usePermissionModules = ({ enabled = true } = {}) => {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: Query_Keys.permission_modules,
    queryFn: async () => {
      const response = await api.get('/v1/admin/permission_modules');
      return response?.data;
    },
    enabled,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  return useMemo(() => {
    const groups = Array.isArray(data?.groups) ? data.groups : [];
    const modules = groups.flatMap((group) => (Array.isArray(group?.modules) ? group.modules : []));
    const moduleKeys = modules.map((module) => module?.key).filter(Boolean);

    const selectableKeys = modules
      .filter((module) => module?.key && !module?.top_level_only)
      .map((module) => module.key);

    return {
      groups,
      moduleKeys,
      moduleKeySet: new Set(moduleKeys),
      selectableKeySet: new Set(selectableKeys),
      isLoading,
      isError,
      refetch,
    };
  }, [data, isLoading, isError, refetch]);
};
